import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ListNotificationsQuery,
  NotificationCategory,
  SetNotificationEmailGovernanceRequest,
  UpdateNotificationPreferencesRequest,
} from "@jema-manager/shared";
import { notificationsApi } from "@shared/api/notifications";
import { queryKeys } from "@shared/lib/react-query";

type UseNotificationsOptions = {
  category?: NotificationCategory;
  unreadOnly?: boolean;
  includeArchived?: boolean;
  limit?: number;
  enabled?: boolean;
};

export const useNotifications = (options: UseNotificationsOptions = {}) => {
  const queryClient = useQueryClient();
  const {
    category,
    unreadOnly,
    includeArchived,
    enabled = true,
    limit = 20,
  } = options;

  const queryParams: ListNotificationsQuery & { includeArchived?: boolean } = {
    limit,
    ...(category ? { category } : {}),
    ...(unreadOnly !== undefined ? { unreadOnly } : {}),
    ...(includeArchived !== undefined ? { includeArchived } : {}),
  };
  const listQueryKey = queryKeys.notifications.list(queryParams);
  const unreadCountQueryKey = queryKeys.notifications.unreadCount();

  const listQuery = useInfiniteQuery({
    queryKey: listQueryKey,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) =>
      notificationsApi.list({
        ...queryParams,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled,
  });

  const unreadCountQuery = useQuery({
    queryKey: unreadCountQueryKey,
    queryFn: notificationsApi.unreadCount,
    enabled,
  });

  const markReadMutation = useMutation({
    mutationFn: notificationsApi.markAsRead,
    onMutate: async (notificationId: string) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: listQueryKey }),
        queryClient.cancelQueries({ queryKey: unreadCountQueryKey }),
      ]);

      const previousList = queryClient.getQueryData(listQueryKey);
      const previousUnread = queryClient.getQueryData(unreadCountQueryKey) as
        | { unreadCount?: number }
        | undefined;

      let shouldDecrementUnread = false;

      queryClient.setQueryData(listQueryKey, (old: any) => {
        if (!old?.pages) return old;

        const pages = old.pages.map((page: any) => {
          const items = (page.items ?? []).map((item: any) => {
            if (item.id !== notificationId) return item;
            if (!item.isRead) {
              shouldDecrementUnread = true;
            }
            return {
              ...item,
              isRead: true,
              readAt: item.readAt ?? new Date().toISOString(),
            };
          });
          return { ...page, items };
        });

        return { ...old, pages };
      });

      if (shouldDecrementUnread) {
        queryClient.setQueryData(unreadCountQueryKey, (old: any) => ({
          ...old,
          unreadCount: Math.max(0, (old?.unreadCount ?? 0) - 1),
        }));
      }

      return { previousList, previousUnread };
    },
    onError: (_error, _variables, context) => {
      if (!context) return;
      queryClient.setQueryData(listQueryKey, context.previousList);
      queryClient.setQueryData(unreadCountQueryKey, context.previousUnread);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all }),
      ]);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: unreadCountQueryKey });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: notificationsApi.markAllAsRead,
    onMutate: async () => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: listQueryKey }),
        queryClient.cancelQueries({ queryKey: unreadCountQueryKey }),
      ]);

      const previousList = queryClient.getQueryData(listQueryKey);
      const previousUnread = queryClient.getQueryData(unreadCountQueryKey);

      queryClient.setQueryData(listQueryKey, (old: any) => {
        if (!old?.pages) return old;
        const now = new Date().toISOString();
        const pages = old.pages.map((page: any) => ({
          ...page,
          items: (page.items ?? []).map((item: any) =>
            item.isRead
              ? item
              : {
                  ...item,
                  isRead: true,
                  readAt: item.readAt ?? now,
                }
          ),
        }));
        return { ...old, pages };
      });

      queryClient.setQueryData(unreadCountQueryKey, (old: any) => ({
        ...old,
        unreadCount: 0,
      }));

      return { previousList, previousUnread };
    },
    onError: (_error, _variables, context) => {
      if (!context) return;
      queryClient.setQueryData(listQueryKey, context.previousList);
      queryClient.setQueryData(unreadCountQueryKey, context.previousUnread);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all }),
      ]);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: unreadCountQueryKey });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: notificationsApi.archive,
    onMutate: async (notificationId: string) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: listQueryKey }),
        queryClient.cancelQueries({ queryKey: unreadCountQueryKey }),
      ]);

      const previousList = queryClient.getQueryData(listQueryKey);
      const previousUnread = queryClient.getQueryData(unreadCountQueryKey);
      let shouldDecrementUnread = false;

      queryClient.setQueryData(listQueryKey, (old: any) => {
        if (!old?.pages) return old;
        const pages = old.pages.map((page: any) => {
          const items = (page.items ?? []).filter((item: any) => {
            if (item.id !== notificationId) return true;
            if (!item.isRead) shouldDecrementUnread = true;
            return false;
          });
          return { ...page, items };
        });
        return { ...old, pages };
      });

      if (shouldDecrementUnread) {
        queryClient.setQueryData(unreadCountQueryKey, (old: any) => ({
          ...old,
          unreadCount: Math.max(0, (old?.unreadCount ?? 0) - 1),
        }));
      }

      return { previousList, previousUnread };
    },
    onError: (_error, _variables, context) => {
      if (!context) return;
      queryClient.setQueryData(listQueryKey, context.previousList);
      queryClient.setQueryData(unreadCountQueryKey, context.previousUnread);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all }),
      ]);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: unreadCountQueryKey });
    },
  });

  return {
    ...listQuery,
    unreadCount: unreadCountQuery.data?.unreadCount ?? 0,
    unreadCountLoading: unreadCountQuery.isLoading,
    notifications: listQuery.data?.pages.flatMap((page) => page.items) ?? [],
    markAsRead: markReadMutation.mutateAsync,
    markAllAsRead: markAllReadMutation.mutateAsync,
    archive: archiveMutation.mutateAsync,
    isUpdating:
      markReadMutation.isPending || markAllReadMutation.isPending || archiveMutation.isPending,
  };
};

export const useNotificationPreferences = () => {
  const queryClient = useQueryClient();
  const preferencesQueryKey = queryKeys.notifications.preferences();

  const preferencesQuery = useQuery({
    queryKey: preferencesQueryKey,
    queryFn: notificationsApi.getPreferences,
  });

  const updateMutation = useMutation({
    mutationFn: (payload: UpdateNotificationPreferencesRequest) =>
      notificationsApi.updatePreferences(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: preferencesQueryKey });
    },
  });

  return {
    ...preferencesQuery,
    updatePreferences: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
  };
};

export const useNotificationUnreadCount = (enabled = true) => {
  return useQuery({
    queryKey: queryKeys.notifications.unreadCount(),
    queryFn: notificationsApi.unreadCount,
    enabled,
  });
};

export const useNotificationEmailGovernance = (userId: string | null, enabled = true) => {
  const queryClient = useQueryClient();
  const governanceQueryKey = userId ? queryKeys.notifications.governance(userId) : [];

  const governanceQuery = useQuery({
    queryKey: governanceQueryKey,
    queryFn: () => notificationsApi.getGovernance(userId!),
    enabled: Boolean(userId && enabled),
  });

  const updateMutation = useMutation({
    mutationFn: (payload: SetNotificationEmailGovernanceRequest) =>
      notificationsApi.setGovernance(userId!, payload),
    onSuccess: async () => {
      if (!userId) {
        return;
      }

      await queryClient.invalidateQueries({ queryKey: queryKeys.notifications.governance(userId) });
    },
  });

  return {
    ...governanceQuery,
    setGovernance: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
  };
};
